/**
 * Display precision for a trading fee, defined once for every fee column.
 *
 * - USD and USD stablecoins: cents (2 dp). A non-zero fee under a cent keeps
 *   2 significant digits instead of rounding to a misleading "0.00".
 * - Anything else (a coin-denominated fee such as BTC or ETH): 4 significant
 *   digits, never more than 8 decimals, so a Deribit fee reads 0.00001235 BTC
 *   and a larger one 1.235 ETH instead of a fixed 8-decimal tail.
 *
 * Returns an en-US string; callers pass it through `useNumberFormat().text`
 * with `{ sensitive: true }` so the separator setting and hide-balances apply.
 */

const USD_LIKE = new Set([
  'USD',
  'USDT',
  'USDC',
  'USDE',
  'USDH',
  'DAI',
  'FDUSD',
  'PYUSD',
  'TUSD',
  'BUSD',
  'USDS',
  'USD1',
])

export function isUsdLikeCurrency(currency: string | null | undefined): boolean {
  return !!currency && USD_LIKE.has(currency.trim().toUpperCase())
}

export function feeFormatOptions(
  fee: number,
  currency: string | null | undefined
): Intl.NumberFormatOptions {
  const abs = Math.abs(fee)
  if (isUsdLikeCurrency(currency)) {
    if (abs > 0 && abs < 0.01) return { maximumSignificantDigits: 2 }
    return { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  }
  if (abs === 0) return { maximumFractionDigits: 0 }
  if (abs >= 1) return { maximumFractionDigits: 4 }
  // Sub-unit coin fee: 4 significant digits, capped at 8 decimals.
  const leadingZeros = Math.max(0, -Math.floor(Math.log10(abs)) - 1)
  return { maximumFractionDigits: Math.min(8, leadingZeros + 4) }
}

export function formatFee(fee: number, currency: string | null | undefined): string {
  return fee.toLocaleString('en-US', feeFormatOptions(fee, currency))
}
